<script>
import SelectBranch from './SelectBranch.vue';
import SelectProduct from './SelectProduct.vue';
import SelectContact from './SelectContact.vue';
import SettingsModal from '../components/SettingsModal.vue';

export default {
  name: 'Filters',
  props: {
    counts: {
      type: Object,
      default: () => ({
        upcoming: 0,
        requested: 0,
        confirmed: 0,
        rescheduled: 0,
        archive: 0,
        cancelled: 0,
        completed: 0,
        future: 0
      })
    }
  },
  components: {
    SelectBranch,
    SelectProduct,
    SelectContact,
    SettingsModal
  },
  data() {
    return {
      status: this.$store.state.rentalBranch.query.status || '',
      start_date: this.$store.state.rentalBranch.query.start_date || '',
      end_date: this.$store.state.rentalBranch.query.end_date || '',
      sort_by: this.$store.state.rentalBranch.query.sort_by || 'date',
      sort_order: this.$store.state.rentalBranch.query.sort_order || 'asc',
      branch_id: this.$store.state.rentalBranch.query.branch_id || '',
      product_id: this.$store.state.rentalBranch.query.product_id || '',
      customer_id: this.$store.state.rentalBranch.query.customer_id || '',
      selectedBranch: null,
      selectedProduct: null,
      selectedCustomer: null,
      statusOptions: [
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'requested', label: 'Requested' },
        { value: 'confirmed', label: 'Confirmed' },
        { value: 'rescheduled', label: 'Rescheduled' },
        { value: 'archive', label: 'Archive' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'completed', label: 'Completed' },
        { value: 'future', label: 'Future' },
      ],
      sortByOptions: [
        { value: 'date', label: 'Rental Date' },
        { value: 'created_at', label: 'Created At' },
      ],
      sortOrderOptions: [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
      ],
      showSettings: false,
    }
  },
  watch: {
    status() { this.emitFilterChange(); },
    start_date() { this.emitFilterChange(); },
    end_date() { this.emitFilterChange(); },
    sort_by() { this.emitFilterChange(); },
    sort_order() { this.emitFilterChange(); },
    branch_id() { this.emitFilterChange(); },
    product_id() { this.emitFilterChange(); },
    customer_id() { this.emitFilterChange(); },
  },
  methods: {
    onBranchSelected(branch) {
      this.selectedBranch = branch;
      this.branch_id = branch ? branch.branch_id : '';
      this.product_id = '';
      this.selectedProduct = null;
    },
    onProductSelected(product) {
      this.selectedProduct = product;
      this.product_id = product ? product.product_id : '';
    },
    onCustomerSelected(customer) {
      this.selectedCustomer = customer;
      this.customer_id = customer ? customer.id : '';
    },
    emitFilterChange() {
      this.$emit('filter-change', {
        status: this.status,
        start_date: this.start_date,
        end_date: this.end_date,
        sort_by: this.sort_by,
        sort_order: this.sort_order,
        branch_id: this.branch_id,
        product_id: this.product_id,
        customer_id: this.customer_id
      });
    },
    async handleResetFilter() {
      await this.$store.dispatch('rentalBranch/resetFilter');
      // Reset local filter state to match store
      const q = this.$store.state.rentalBranch.query;
      this.status = this.$store.state.rentalBranch.trackingStatus || '';
      this.start_date = q.start_date || '';
      this.end_date = q.end_date || '';
      this.sort_by = q.sort_by || 'date';
      this.sort_order = q.sort_order || 'asc';
      this.branch_id = q.branch_id || '';
      this.product_id = q.product_id || '';
      this.customer_id = q.customer_id || '';
      this.selectedBranch = null;
      this.selectedProduct = null;
      this.selectedCustomer = null;
      this.emitFilterChange();
    }
  },
  mounted() {
    this.$store.dispatch('rentalBranch/fetchTrackingStatus');
    this.status = this.$store.state.rentalBranch.trackingStatus || 'requested';
  }
}
</script>

<template>
  <div class="w-full">
    <!-- Modals -->
    <SettingsModal v-if="showSettings" @close="showSettings = false" />
    <!-- 1) Status Filter Chips + Reset Button -->
    <div class="flex flex-wrap gap-2 mb-4 items-center">
      <div
        v-for="opt in statusOptions"
        :key="opt.value"
        @click="status = opt.value"
        class="px-4 py-1 rounded-full cursor-pointer transition-all duration-200 ease-in-out border"
        :class="{
          'bg-woot-500 text-white border-woot-500 hover:bg-woot-300': status === opt.value,
          'bg-white dark:bg-slate-700 text-slate-700 dark:text-white border-slate-500 hover:bg-slate-100 dark:hover:bg-slate-500': status !== opt.value
        }"
      >
        {{ opt.label }} ({{ counts[opt.value] || 0 }})
      </div>
      <div 
        class="flex items-center px-4 py-1 rounded-full cursor-pointer transition-all duration-200 ease-in-out border bg-white dark:bg-slate-700 text-slate-700 dark:text-white border-slate-500 hover:bg-slate-100 dark:hover:bg-slate-500"
        @click="showSettings = true"
        title="Filter Settings"
      >
        <fluent-icon icon="settings" />
      </div>
      <button
        class="ml-2 px-4 py-1 rounded-full border border-slate-400 bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all"
        @click="handleResetFilter"
        type="button"
        title="Reset Filters"
      >
        Reset
      </button>
    </div>

    <!-- 2) Branch / Product / Customer Selects -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
      <div>
        <SelectBranch @selected="onBranchSelected" v-model="selectedBranch" />
      </div>
      <div>
        <SelectProduct :selectedBranchId="branch_id" @selected="onProductSelected" />
      </div>
      <div>
        <SelectContact @selected="onCustomerSelected" v-model="selectedCustomer" />
      </div>
    </div>

    <!-- 3) Date Range + Sort Options -->
    <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end mb-4">
      <div>
        <label class="block text-sm font-medium mb-1">From</label>
        <input
          v-model="start_date"
          type="date"
          class="w-full border rounded px-2 py-1"
        />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">To</label>
        <input
          v-model="end_date"
          type="date"
          class="w-full border rounded px-2 py-1"
        />
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">Sort by</label>
        <select
          v-model="sort_by"
          class="w-full border rounded px-2 py-1"
        >
          <option
            v-for="o in sortByOptions"
            :key="o.value"
            :value="o.value"
          >
            {{ o.label }}
          </option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium mb-1">Order</label>
        <select
          v-model="sort_order"
          class="w-full border rounded px-2 py-1"
        >
          <option
            v-for="o in sortOrderOptions"
            :key="o.value"
            :value="o.value"
          >
            {{ o.label }}
          </option>
        </select>
      </div>
    </div>
  </div>
</template>
